import { Injectable } from '@angular/core';
import { UserAccountDTO } from "../../shared/object/xplorer-be-parsed-classes";
import {environment} from "../../../environments/environment";

const TOKEN_KEY = 'auth-token';
const USER_KEY = 'auth-user';
const REFRESH_TOKEN = 'refresh-token';

type JwtPayload = {
  exp?: number;
  iat?: number;
  sub?: string;
  resource_access?: Record<string, { roles: string[] }>;
  session_state?: string;
  realm_access?: { roles: string[] };
  [key: string]: unknown;
};

@Injectable({
  providedIn: 'root'
})
export class TokenStorageService {

  constructor() { }

  signOut(): void {
    if (!this.isStorageAvailable()) {
      return;
    }
    this.removeItem(TOKEN_KEY);
    this.removeItem(REFRESH_TOKEN);
    this.removeItem(USER_KEY);
  }

  public saveRefreshToken(token: string): void {
    if (!this.isStorageAvailable()) {
      return;
    }
    localStorage.setItem(REFRESH_TOKEN, token);
  }

  public getRefreshToken(): string | null {
    if (!this.isStorageAvailable()) {
      return null;
    }
    return localStorage.getItem(REFRESH_TOKEN);
  }

  public saveToken(token: string): void {
    if (!this.isStorageAvailable()) {
      return;
    }
    localStorage.setItem(TOKEN_KEY, token);
  }

  public getToken(): string | null {
    if (!this.isStorageAvailable()) {
      return null;
    }
    return localStorage.getItem(TOKEN_KEY);
  }

  public removeToken(): void {
    this.removeItem(TOKEN_KEY);
  }

  public removeRefreshToken(): void {
    this.removeItem(REFRESH_TOKEN);
  }

  public saveUser(user: UserAccountDTO): void {
    if (!this.isStorageAvailable()) {
      return;
    }
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  }

  public getUser(): UserAccountDTO | null {
    if (!this.isStorageAvailable()) {
      return null;
    }
    const item = localStorage.getItem(USER_KEY);
    if (item) {
      try {
        return JSON.parse(item) as UserAccountDTO;
      } catch {
        this.removeItem(USER_KEY);
      }
    }

    return null;
  }

  public removeUser = () => {
    if (!this.isStorageAvailable()) {
      return;
    }
    localStorage.removeItem(USER_KEY);
  }

  public isTokenExpired(token: string | null | undefined): boolean {

    if (!token || token === 'null' || token === 'undefined') {
      return true;
    }

    const tokenPayload = this.getTokenPayload(token);
    if (typeof tokenPayload?.exp !== 'number') {
      return true;
    }
    return Math.floor(new Date().getTime() / 1000) >= tokenPayload?.exp;
  }

  public getTokenPayload(token: string | null | undefined): JwtPayload | null {
    if(!token) {
      return null;
    }
    if (typeof atob === 'undefined') {
      return null;
    }

    const tokenParts = token.split('.');
    if (tokenParts.length < 2 || !tokenParts[1]) {
      return null;
    }

    try {
      const normalizedPayload = this.normalizeBase64UrlSegment(tokenParts[1]);
      return JSON.parse(atob(normalizedPayload)) as JwtPayload;
    } catch {
      return null;
    }
  }

  public getRolesFromToken(token: string | null | undefined): string[] {
    const tokenPayload = this.getTokenPayload(token);
    const resourceAccess = tokenPayload?.resource_access?.[environment.KEYCLOAK_CLIENT_ID];
    return resourceAccess?.roles ?? [];
  }

  private normalizeBase64UrlSegment(segment: string): string {
    const paddedLength = Math.ceil(segment.length / 4) * 4;
    return segment
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(paddedLength, '=');
  }

  private isStorageAvailable(): boolean {
    return typeof localStorage !== 'undefined';
  }

  private removeItem(key: string): void {
    if (!this.isStorageAvailable()) {
      return;
    }
    localStorage.removeItem(key);
  }
}
